import { useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { AppSidebar } from '@/components/app-sidebar';
import { SearchDialog } from '@/components/search-dialog';
import { FaktLogo } from '@/components/brand';
import { InstallPrompt } from '@/components/install-prompt';
import { MobileMenu, MobileNav } from '@/components/mobile-nav';
import { Moon, Search, Sun } from 'lucide-react';
import { useAuth } from '@/lib/auth-context';
import { istBereichGesperrt, usePermissions } from '@/lib/use-permissions';
import { useTheme } from '@/lib/theme-context';
import { NAV, groupBySection } from '@/components/nav-items';
export type { NavItem } from '@/components/nav-items';

export function AppLayout() {
  const { user, logout } = useAuth();
  const permissions = usePermissions();
  const { theme, toggle: toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  // Ctrl+K / Cmd+K öffnet die globale Suche
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // BL-263: gesperrte Bereiche werden aus der Sidebar gefiltert. Wer per
  // direktem URL-Aufruf trotzdem hinkommt, bekommt den Backend-403 (kein
  // zusätzlicher Frontend-Guard nötig, siehe docs/BL-263-architektur.md).
  const visibleNav = NAV.filter(
    (item) => !item.bereich || !istBereichGesperrt(permissions, item.bereich),
  );
  const sections = groupBySection(visibleNav);

  return (
    // BL-161: h-screen statt min-h-screen — ohne feste Höhe hatte `<main>`s
    // `overflow-hidden` unten nie eine Grenze zum Clippen, wodurch die ganze
    // Seite (statt nur des inneren Content-Containers) scrollte und die
    // Kopfzeile mit wegscrollte.
    <div className="flex h-screen bg-bg-alt text-ink">
      {/* BL-297: einzige, klassisch einklappbare Sidebar mit Gruppen-Headern
          löst die vorherige BL-284-Icon-Schiene + separates Bereichs-Panel ab
          (zwei Navigations-Ebenen brauchten laut User-Feedback zu viele
          Klicks). Ausgelagert nach app-sidebar.tsx (BL-112-Zeilenschranke +
          eigenständiger State). Auf Mobile (<md) blendet sie sich selbst
          aus, dort uebernimmt MobileNav + MobileMenu (bekommt `sections`
          unveraendert in der alten flachen Form). */}
      <AppSidebar sections={sections} />

      {/* ================ MAIN ========================================= */}
      <main className="flex flex-1 flex-col overflow-hidden">
        {/* Top-Bar: Mobile zeigt nur die Brand-Wordmark (Suche ist im
            BottomNav). Desktop nur noch die Search-Box (BL-161: Bell-Button
            und das wirkungslose Avatar-Icon entfernt — beide echten
            User-Einstiegspunkte, Sidebar-Fußzeile + Mobile-Menü, bleiben
            unverändert). */}
        <div className="flex h-[54px] items-center gap-3 border-b border-hair bg-panel px-4 md:px-6">
          {/* Mobile-Brand statt Search-Box: FAKT-Produkt-Wordmark (Icon-konsistent) */}
          <div className="flex items-center md:hidden">
            <FaktLogo size={22} />
          </div>
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            // BL-161: max-w erhöht (vorher 380px) — der Bell-Button und das
            // wirkungslose Avatar-Icon rechts sind entfallen, die Suche bekommt
            // den freiwerdenden Platz statt ihn ungenutzt zu lassen.
            className="hidden max-w-[560px] flex-1 items-center gap-2 border border-hair-strong bg-panel px-3 py-[7px] text-left text-[13px] text-ink-muted transition-colors hover:bg-bg-alt md:inline-flex"
          >
            <Search className="h-4 w-4" />
            {/* Fix 12.08.2026: stand vorher „…, Betrag …" — das Backend
                durchsucht aber kein Betragsfeld (routes/search.ts: reNr, kunde,
                projekt, consultant, verwendungszweck). Eine Betragssuche
                lieferte deshalb immer „keine Treffer", was die Rechnung als
                nicht vorhanden erscheinen ließ. Text an die Realität angepasst
                statt ein Versprechen stehen zu lassen. */}
            <span className="flex-1 truncate">Rechnungs-Nr., Kunde, Projekt …</span>
            <kbd className="border border-hair-strong bg-panel px-1.5 py-[1px] font-mono text-[10px] text-ink-muted">
              Strg K
            </kbd>
          </button>
        </div>
        {/* Page-Content. pb-16 reserviert Platz fuer die fixed-positionierte
             MobileNav unter md:; ab md: kein Extra-Padding.
             BL-284: Cap von 1280px auf 1600px gelockert (nicht entfernt) —
             der eigentliche Flaschenhals fuer breite Tabellenseiten (Konten-
             Explorer, Abrechnungszeitraeume-Matrix, siehe BL-282) war die
             Kombination aus Sidebar-Breite UND diesem Cap; die einklappbare
             Sidebar (BL-297) gibt bei Bedarf zusaetzlich bis zu 180px frei
             (232px ausgeklappt → 52px Icon-only-Rail eingeklappt). */}
        <div className="flex-1 overflow-auto bg-bg-alt">
          <div className="mx-auto max-w-[1600px] p-4 pb-16 md:p-7 md:pb-7">
            <Outlet />
          </div>
        </div>
      </main>

      <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />

      {/* PWA-Install-Banner (Desktop Chrome/Edge) — fired by browser ueber
          beforeinstallprompt. iOS Safari muss "Zum Home-Bildschirm" manuell. */}
      <InstallPrompt />

      {/* Mobile-Only: Bottom-Nav + Menue-Sheet. Beide haben intern md:hidden. */}
      <MobileNav
        onSearchClick={() => setSearchOpen(true)}
        onMenuClick={() => setMobileMenuOpen(true)}
      />
      <MobileMenu
        open={mobileMenuOpen}
        onOpenChange={setMobileMenuOpen}
        sections={sections}
        userName={user?.name ?? '—'}
        userRole={user?.role === 'ADMIN' ? 'Admin' : user?.role === 'USER' ? 'Buchhalter' : 'Leser'}
        onLogout={() => {
          setMobileMenuOpen(false);
          handleLogout();
        }}
        onThemeToggle={toggleTheme}
        themeIcon={
          theme === 'dark' ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />
        }
        themeLabel={theme === 'dark' ? 'Hell' : 'Dunkel'}
      />
    </div>
  );
}
